<template>
  <div class="w-full p-6">
    <!-- Header Section -->
    <div class="mb-6">
      <div class="flex justify-between items-center mb-2">
        <h1 class="text-2xl font-bold text-primary">Dashboard</h1>
        <div>
          <button
            v-if="canTriggerForecast"
            @click="openNewForecastModal"
            class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
          >
            <svg class="h-5 w-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            New Forecast
          </button>
        </div>
      </div>
      <p class="text-muted-foreground">Overview of tasks, activities, and recent updates</p>
    </div>

    <!-- Reporting Periods Section -->
    <div class="mb-6">
      <div v-if="forecastingProcesses.length > 0" class="space-y-4">
        <div v-for="process in forecastingProcesses" :key="process.id" class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-xl font-bold">{{ process.name }} Reporting Period</h2>
          <p class="text-gray-600 mb-2">
            {{ formatDate(process.start_date) }} - {{ formatDate(process.end_date) }}
            <span v-if="getDaysRemaining(process.end_date) > 0">
              ({{ getDaysRemaining(process.end_date) }} days remaining)
            </span>
            <span v-else class="text-red-500">
              (Overdue)
            </span>
          </p>
          <div class="w-full bg-gray-200 rounded-full h-2.5">
            <div
              class="bg-blue-600 h-2.5 rounded-full"
              :style="{ width: getProgressPercentage(process.start_date, process.end_date) + '%' }"
            ></div>
          </div>
        </div>
      </div>
      <div v-else class="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
        <div class="flex flex-col items-center">
          <div class="mb-4 p-3 bg-gray-100 rounded-full">
            <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Active Reporting Periods</h3>
          <p class="text-gray-500 mb-6">Start a new forecast process to begin tracking reporting periods</p>
          <button
            @click="openNewForecastModal"
            v-if="canTriggerForecast"
            class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Start New Forecast
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Tasks Section -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div class="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 class="text-xl font-semibold">Tasks</h2>
              <p class="text-sm text-gray-500">Manage and track reporting tasks</p>
            </div>
            <button class="p-2 rounded-md hover:bg-gray-100">
              <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>
          
          <!-- Task Tabs -->
          <div class="border-b border-gray-200">
            <div class="flex">
              <button
                @click="activeTab = 'my-tasks'"
                class="px-4 py-2 border-b-2 font-medium transition-colors duration-200"
                :class="activeTab === 'my-tasks' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'"
              >
                My Tasks
              </button>
              <button
                @click="activeTab = 'all-tasks'"
                class="px-4 py-2 border-b-2 font-medium transition-colors duration-200"
                :class="activeTab === 'all-tasks' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'"
              >
                All Tasks
              </button>
            </div>
          </div>
          
          <!-- Task Table -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planner</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Modified</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                  <th scope="col" class="relative px-6 py-3">
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <template v-for="task in activeTab === 'my-tasks' ? myTasks : allTasks" :key="task.id">
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900">{{ task.report }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-500">{{ task.planner }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-500">{{ task.lastModified }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm" :class="task.dueDateClass">{{ task.dueDate }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="task.stateClass">
                        {{ task.state }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        @click="toggleTaskExpanded(task.id)"
                        class="text-primary hover:text-primary-dark transition-transform duration-200"
                        :class="{ 'transform rotate-180': expandedTaskId === task.id }"
                      >
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                  
                  <!-- Expanded Actions Section -->
                  <tr v-if="expandedTaskId === task.id">
                    <td colspan="6" class="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <div class="flex space-x-3">
                        <button class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                          <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          Approve
                        </button>
                        <button class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                          <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                          </svg>
                          Request Changes
                        </button>
                        <button class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                          <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                          </svg>
                          Add Comment
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
                
                <!-- Empty state when no tasks are available -->
                <tr v-if="(activeTab === 'my-tasks' && myTasks.length === 0) || (activeTab === 'all-tasks' && allTasks.length === 0)">
                  <td colspan="6" class="px-6 py-10 text-center">
                    <p class="text-gray-500">No tasks available</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- No Available Actions section as requested -->
        </div>
      </div>
      
      <!-- Activity Feed Section -->
      <div class="lg:col-span-1">
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div class="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 class="text-xl font-semibold">Activity Feed</h2>
              <p class="text-sm text-gray-500">Recent actions by team members</p>
            </div>
          </div>
          
          <div class="p-4">
            <ul class="space-y-4">
              <!-- Activity 1 -->
              <li class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                    L
                  </div>
                </div>
                <div>
                  <p class="text-sm">
                    <span class="font-medium">Luka Novak</span> 
                    <span class="text-gray-500"> uploaded </span> 
                    <span class="font-medium">Quarterly Forecast Report 1</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    10 minutes ago
                  </p>
                </div>
              </li>
              
              <!-- Activity 2 -->
              <li class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                    M
                  </div>
                </div>
                <div>
                  <p class="text-sm">
                    <span class="font-medium">Maja Kovač</span> 
                    <span class="text-gray-500"> downloaded </span> 
                    <span class="font-medium">Q2 2025 Template</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    25 minutes ago
                  </p>
                </div>
              </li>
              
              <!-- Activity 3 -->
              <li class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                    J
                  </div>
                </div>
                <div>
                  <p class="text-sm">
                    <span class="font-medium">Janez Horvat</span> 
                    <span class="text-gray-500"> approved </span> 
                    <span class="font-medium">Nina's Q1 Report</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    1 hour ago
                  </p>
                </div>
              </li>
              
              <!-- Activity 4 -->
              <li class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                    N
                  </div>
                </div>
                <div>
                  <p class="text-sm">
                    <span class="font-medium">Nina Zupančič</span> 
                    <span class="text-gray-500"> rejected </span> 
                    <span class="font-medium">Janez's Q2 Forecast</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    2 hours ago
                  </p>
                </div>
              </li>
              
              <!-- Activity 5 -->
              <li class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                    M
                  </div>
                </div>
                <div>
                  <p class="text-sm">
                    <span class="font-medium">Matej Krajnc</span> 
                    <span class="text-gray-500"> commented on </span> 
                    <span class="font-medium">Q1 2025 Summary</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    3 hours ago
                  </p>
                </div>
              </li>
              
              <!-- Activity 6 -->
              <li class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                    A
                  </div>
                </div>
                <div>
                  <p class="text-sm">
                    <span class="font-medium">Ana Vidmar</span> 
                    <span class="text-gray-500"> flagged </span> 
                    <span class="font-medium">inconsistency in Q3 data</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    5 hours ago
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- New Forecast Modal -->
  <div v-if="showForecastModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
      <!-- Close button -->
      <button
        @click="showForecastModal = false"
        class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
      >
        <span class="text-xl">×</span>
      </button>
      
      <h2 class="text-xl font-bold mb-2">Create New Reporting Period</h2>
      <p class="text-gray-600 mb-6">Set up a new reporting period by providing a name and date range.</p>
      
      <div class="mb-4">
        <label for="periodName" class="block text-sm font-medium text-gray-700 mb-1">Period Name</label>
        <input
          type="text"
          id="periodName"
          v-model="periodName"
          placeholder="e.g. Q3 2025"
          class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
      </div>
      
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="startDate" class="block text-sm text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              id="startDate"
              v-model="startDate"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
          </div>
          <div>
            <label for="endDate" class="block text-sm text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              id="endDate"
              v-model="endDate"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
          </div>
        </div>
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showForecastModal = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="confirmCreateForecast"
          class="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Create Period</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Confirmation Dialog -->
  <div v-if="showConfirmDialog" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
      <h3 class="text-lg font-medium mb-3">Confirm Forecast Creation</h3>
      <p class="text-gray-600 mb-6">Are you sure you want to create a new forecasting process? This action cannot be undone.</p>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showConfirmDialog = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="startForecastProcess"
          class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Yes, Create</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onUnmounted } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { supabase } from '@/services/supabase';
import { BusinessRole } from '@/types/businessRole';
import { ProcessStatus } from '@/types/processStatus';
import { DocumentStatus, DocumentType } from '@/types/supabase';

export default defineComponent({
  name: 'DashboardView',
  setup() {
    // Auth
    const { user, profile } = useAuth();
    
    // Check if user has Head of PCO role
    const canTriggerForecast = computed(() => {

      return true || profile.value?.business_role === BusinessRole.HEAD_OF_PCO;
    });
    
    // Store for all forecasting processes
    const forecastingProcesses = ref<any[]>([]);
    
    // Modal state
    const showForecastModal = ref(false);
    const showConfirmDialog = ref(false);
    
    // Process state
    const processStatus = ref<string | null>(null); // NULL, 'PENDING', 'PROCESSING', 'COMPLETED', 'ERROR'
    const processId = ref<string | null>(null);
    const statusPollingInterval = ref<number | null>(null);
    const isLoading = ref(false);
    
    // Form data
    const periodName = ref('');
    const startDate = ref('');
    const endDate = ref('');
    
    // Simple notification function (can be replaced with a proper notification system later)
    const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
      console.log(`[${type.toUpperCase()}] ${message}`);
      // In a real implementation, this would show a UI notification
    };
    
    // Open the modal
    const openNewForecastModal = () => {
      // Reset form data
      periodName.value = '';
      startDate.value = '';
      endDate.value = '';
      showForecastModal.value = true;
    };
    
    // Show confirmation dialog
    const confirmCreateForecast = () => {
      showConfirmDialog.value = true;
    };
    
    // Function to start polling for status updates
    const startStatusPolling = (id: string) => {
      // Clear any existing interval
      if (statusPollingInterval.value) {
        clearInterval(statusPollingInterval.value);
      }
      
      // Set up polling every 5 seconds
      statusPollingInterval.value = window.setInterval(async () => {
        try {
          const { data, error } = await supabase
            .from('forecasting_process')
            .select('id, status')
            .eq('id', id)
            .single();
            
          if (error) throw error;
          
          processStatus.value = data.status;
          
          // If process is completed or errored, stop polling
          if (data.status === ProcessStatus.CREATED || data.status === ProcessStatus.COMPLETED) {
            if (statusPollingInterval.value) {
              clearInterval(statusPollingInterval.value);
            }
            
            // Show appropriate notification
            if (data.status === ProcessStatus.COMPLETED) {
              showNotification('Forecast process completed successfully');
            }
            
            // Refresh the list of forecasting processes
            fetchForecastingProcesses();
          }
        } catch (error) {
          console.error('Error polling for status:', error);
          if (statusPollingInterval.value) {
            clearInterval(statusPollingInterval.value);
          }
        }
      }, 5000);
    };
    
    // Start the forecast process
    const startForecastProcess = async () => {
      // Set loading state
      isLoading.value = true;
      
      try {
        if (!user.value) {
          // showNotification('User not authenticated', 'error');
          // return;
        }

        // Format dates as ISO strings for timestamp
        const formattedStartDate = startDate.value ? new Date(startDate.value).toISOString() : null;
        const formattedEndDate = endDate.value ? new Date(endDate.value).toISOString() : null;
        
        // 1. Create the forecasting process
        const { data, error } = await supabase
          .from('forecasting_process')
          .insert({
            name: periodName.value,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            status: ProcessStatus.CREATED
          })
          .select();
          
        if (error) throw error;
        
        const newProcessId = data[0].id;
        
        // Process the response
        processId.value = newProcessId;
        processStatus.value = ProcessStatus.CREATED;
        
        // Close confirmation dialog but keep modal open
        showConfirmDialog.value = false;

        // For the moment, we do not have the planners yet, so this is mock code
        // It will create two documents and assign them to the current user himself
        const { data: userData } = await supabase
          .from('profiles')
          .select('id')
          .single();
        const currentUserId = userData?.id;
        const responsiblePlannerIds = [currentUserId, currentUserId];
        
        // Create documents for each planner using the document entity
        const documentInserts = responsiblePlannerIds.map(responsiblePlannerId => ({
          name: `${periodName.value} - Template`,
          status: DocumentStatus.CREATING,
          type: DocumentType.SALES_REPORT,
          created_by: responsiblePlannerId,
          forecast_id: newProcessId,
          created_at: new Date().toISOString()
        }));
        
        const { error: documentsError } = await supabase
          .from('documents')
          .insert(documentInserts);
        
        if (documentsError) {
          console.error('Error creating documents:', documentsError);
          throw documentsError;
        }
        showNotification('Forecast process started successfully');
        
        // Start polling for status updates
        startStatusPolling(newProcessId);
        
        // Fetch updated data from backend
        await fetchForecastingProcesses();
        
        // Now close the modal
        showForecastModal.value = false;
      } catch (error) {
        console.error('Error starting forecast process:', error);
        processStatus.value = 'error';
        showNotification('Failed to start forecast process', 'error');
      } finally {
        // Reset loading state
        isLoading.value = false;
      }
    };
    
    // Function to fetch all forecasting processes
    const fetchForecastingProcesses = async () => {
      try {
        const { data, error } = await supabase
          .from('forecasting_process')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        forecastingProcesses.value = data || [];
        
        // If there's an active process, update the status indicator
        const activeProcess = data?.find(p => p.status === ProcessStatus.CREATED);
        if (activeProcess) {
          processId.value = activeProcess.id;
          processStatus.value = activeProcess.status;
          
          // Start polling for status updates
          startStatusPolling(activeProcess.id);
        }
      } catch (error) {
        console.error('Error fetching forecasting processes:', error);
      }
    };
    
    // Helper function to format dates
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };
    
    // Helper function to calculate days remaining
    const getDaysRemaining = (endDateString: string) => {
      if (!endDateString) return 0;
      
      const endDate = new Date(endDateString);
      const today = new Date();
      
      // Reset time part for accurate day calculation
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    };
    
    // Helper function to calculate progress percentage
    const getProgressPercentage = (startDateString: string, endDateString: string) => {
      if (!startDateString || !endDateString) return 0;
      
      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);
      const today = new Date();
      
      // Reset time part for accurate day calculation
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const totalDuration = endDate.getTime() - startDate.getTime();
      const elapsedDuration = today.getTime() - startDate.getTime();
      
      if (totalDuration <= 0) return 100;
      
      let percentage = (elapsedDuration / totalDuration) * 100;
      
      // Ensure percentage is between 0 and 100
      percentage = Math.max(0, Math.min(100, percentage));
      
      return percentage;
    };
    
    // Fetch forecasting processes on component mount
    fetchForecastingProcesses();
    
    // Clean up on component unmount
    onUnmounted(() => {
      if (statusPollingInterval.value) {
        clearInterval(statusPollingInterval.value);
      }
    });
    
    // Active tab state
    const activeTab = ref('my-tasks');
    
    // Track which task is expanded
    const expandedTaskId = ref<number | null>(null);
    
    // Function to toggle task expansion
    const toggleTaskExpanded = (taskId: number) => {
      if (expandedTaskId.value === taskId) {
        // If clicking on the already expanded task, collapse it
        expandedTaskId.value = null;
      } else {
        // Otherwise, expand the clicked task
        expandedTaskId.value = taskId;
      }
    };
    
    // Sample tasks data
    const myTasks = [
      {
        id: 1,
        report: 'Q1 2025 Financial Forecast',
        planner: 'Janez Novak',
        lastModified: '2 hours ago',
        dueDate: 'March 15, 2025',
        dueDateClass: 'text-red-500', // Overdue
        state: 'Review',
        stateClass: 'bg-blue-100 text-blue-800'
      },
      {
        id: 2,
        report: 'Monthly Variance Analysis',
        planner: 'Maja Kovač',
        lastModified: 'Yesterday',
        dueDate: 'April 22, 2025',
        dueDateClass: 'text-gray-500',
        state: 'Email Pending',
        stateClass: 'bg-blue-100 text-blue-800'
      },
      {
        id: 3,
        report: 'Cash Flow Projection',
        planner: 'Eva Golob',
        lastModified: '4 days ago',
        dueDate: 'April 22, 2025',
        dueDateClass: 'text-gray-500',
        state: 'Ready For Revision',
        stateClass: 'bg-green-100 text-green-800'
      }
    ];
    
    const allTasks = [
      ...myTasks,
      {
        id: 4,
        report: 'Quarterly Tax Estimation',
        planner: 'Gregor Kovačič',
        lastModified: '3 days ago',
        dueDate: 'April 8, 2025',
        dueDateClass: 'text-gray-500',
        state: 'Review',
        stateClass: 'bg-blue-100 text-blue-800'
      },
      {
        id: 5,
        report: 'Investment Performance Analysis',
        planner: 'Petra Novak',
        lastModified: '6 days ago',
        dueDate: 'April 25, 2025',
        dueDateClass: 'text-gray-500',
        state: 'Email Pending',
        stateClass: 'bg-blue-100 text-blue-800'
      }
    ];
    
    return {
      activeTab,
      expandedTaskId,
      toggleTaskExpanded,
      myTasks,
      allTasks,
      // Forecast related
      canTriggerForecast,
      showForecastModal,
      showConfirmDialog,
      periodName,
      startDate,
      endDate,
      processStatus,
      isLoading,
      forecastingProcesses,
      formatDate,
      getDaysRemaining,
      getProgressPercentage,
      openNewForecastModal,
      confirmCreateForecast,
      startForecastProcess
    };
  }
});
</script>
